import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "preview", "placeholder"]
  static values = { url: String, nextIndex: Number, nextPosition: Number, single: Boolean, accept: String }

  connect() {
    this.uploadUrl = this.urlValue || "/rails/active_storage/direct_uploads"
    this.photoIndex = this.nextIndexValue || 0
    this.photoPosition = this.nextPositionValue || 0
  }

  dragover(event) {
    event.preventDefault()
    this.element.classList.add("border-blue-500", "bg-blue-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.element.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.element.classList.remove("border-blue-500", "bg-blue-50")
    this.handleFiles(event.dataTransfer.files)
  }

  browse() {
    this.inputTarget.click()
  }

  change(event) {
    this.handleFiles(event.target.files)
    // Reset input so same file can be selected again
    event.target.value = ""
  }

  handleFiles(fileList) {
    const files = Array.from(fileList)

    if (this.singleValue) {
      const file = files.find(f => this.isAcceptedType(f))
      if (file) {
        this.previewTarget.querySelectorAll(".relative").forEach(el => el.remove())
        this.uploadFile(file)
      }
    } else {
      files.forEach(file => {
        if (this.isAcceptedType(file)) {
          this.uploadFile(file)
        }
      })
    }
  }

  isAcceptedType(file) {
    if (this.hasAcceptValue) {
      return this.acceptValue.split(",").some(type => {
        type = type.trim()
        if (type.startsWith(".")) return file.name.toLowerCase().endsWith(type)
        if (type.endsWith("/*")) return file.type.startsWith(type.replace("/*", "/"))
        return file.type === type
      })
    }
    return file.type.startsWith("image/")
  }

  uploadFile(file) {
    const preview = this.createPreview(file)

    const upload = new DirectUpload(file, this.uploadUrl, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", event => {
          const progress = (event.loaded / event.total) * 100
          this.updateProgress(preview, progress)
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        preview.remove()
        console.error("Upload error:", error)
      } else {
        this.uploadComplete(preview, blob)
      }
    })
  }

  createPreview(file) {
    const isVideo = file.type.startsWith("video/")
    const wrapper = document.createElement("div")
    wrapper.className = isVideo ? "relative group" : "relative group cursor-grab active:cursor-grabbing"
    if (!isVideo) wrapper.dataset.sortableItem = ""

    wrapper.innerHTML = `
      <div class="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
        <div class="progress-bar absolute inset-0 bg-blue-500/20"></div>
        ${isVideo ? '' : '<img class="w-full h-full object-cover opacity-50" />'}
      </div>
      <button type="button"
              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-action="click->dropzone#removePreview">
        &times;
      </button>
    `

    if (isVideo) {
      const container = wrapper.querySelector(".w-24")
      const icon = document.createElement("div")
      icon.className = "w-full h-full flex flex-col items-center justify-center text-gray-400 opacity-50"
      icon.innerHTML = `
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
        </svg>
        <span class="text-xs mt-1 truncate max-w-full px-1">${file.name.substring(0, 15)}</span>
      `
      container.appendChild(icon)
    } else {
      const img = wrapper.querySelector("img")
      const reader = new FileReader()
      reader.onload = (e) => img.src = e.target.result
      reader.readAsDataURL(file)
    }

    this.previewTarget.appendChild(wrapper)
    this.updatePlaceholder()

    return wrapper
  }

  updateProgress(preview, percent) {
    const progressBar = preview.querySelector(".progress-bar")
    if (progressBar) {
      progressBar.style.width = `${percent}%`
    }
  }

  uploadComplete(preview, blob) {
    const img = preview.querySelector("img")
    const videoIcon = preview.querySelector(".text-gray-400")
    if (img) img.classList.remove("opacity-50")
    if (videoIcon) videoIcon.classList.remove("opacity-50")

    const progressBar = preview.querySelector(".progress-bar")
    if (progressBar) progressBar.remove()

    const fieldName = this.inputTarget.name

    if (fieldName) {
      // Direct attachment mode: single hidden field with blob signed ID
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = fieldName
      input.value = blob.signed_id
      preview.appendChild(input)
    } else {
      // Nested attributes mode: indexed image + position fields
      const imageInput = document.createElement("input")
      imageInput.type = "hidden"
      imageInput.name = `room[photos_attributes][${this.photoIndex}][image]`
      imageInput.value = blob.signed_id
      imageInput.dataset.blobId = blob.id
      preview.appendChild(imageInput)

      const positionInput = document.createElement("input")
      positionInput.type = "hidden"
      positionInput.name = `room[photos_attributes][${this.photoIndex}][position]`
      positionInput.value = this.photoPosition
      positionInput.dataset.positionField = ""
      preview.appendChild(positionInput)

      preview.dataset.blobId = blob.id
      this.photoIndex++
      this.photoPosition++
    }
  }

  removePreview(event) {
    event.preventDefault()
    event.stopPropagation()
    const preview = event.currentTarget.closest(".relative")
    preview.remove()
    this.updatePlaceholder()
    this.triggerPositionUpdate()
  }

  removeExistingPhoto(event) {
    event.preventDefault()
    event.stopPropagation()
    const preview = event.currentTarget.closest(".relative")
    const destroyField = preview.querySelector("[data-destroy-field]")
    if (destroyField) {
      destroyField.value = "true"
      preview.classList.add("hidden")
      this.updatePlaceholder()
      this.triggerPositionUpdate()
    }
  }

  updatePlaceholder() {
    if (this.hasPlaceholderTarget) {
      const visiblePhotos = this.previewTarget.querySelectorAll(".relative:not(.hidden)")
      const hasPhotos = visiblePhotos.length > 0
      this.placeholderTarget.classList.toggle("hidden", hasPhotos)
    }
  }

  triggerPositionUpdate() {
    const sortableController = this.application.getControllerForElementAndIdentifier(
      this.previewTarget,
      "photo-sortable"
    )
    if (sortableController) {
      sortableController.updatePositions()
    }
  }
}
